import { Color } from '@pixi/color';
import { Graphics } from 'pixi.js-legacy';

import { OrderbookSide } from '@exchange/libs/orderbook/service/src';
import { themingInstance } from '@exchange/libs/utils/theming/src';

import { DepthChartPriceLevel } from '../interfaces';

export default class Graph {
  public largestSizeItem: DepthChartPriceLevel | undefined;

  public lastVisibleItem: DepthChartPriceLevel | undefined;

  private deltaSize = 0;

  private deltaPrice = 0;

  private firstStepSizeInPX = 6;

  private x: number;

  private y: number;

  private width: number;

  private height: number;

  private isAsk: boolean;

  private arrayToDraw: Array<DepthChartPriceLevel>;

  private lastMatchedPrice: number;

  constructor({
    x,
    y,
    width,
    height,
    isAsk,
    arrayToDraw,
    lastMatchedPrice,
  }: {
    x: number;
    y: number;
    width: number;
    height: number;
    isAsk: boolean;
    arrayToDraw: Array<DepthChartPriceLevel>;
    lastMatchedPrice: number;
  }) {
    this.x = x;
    this.y = y;
    this.width = width;
    this.height = height;
    this.isAsk = isAsk;
    this.arrayToDraw = arrayToDraw;
    this.lastMatchedPrice = lastMatchedPrice;
  }

  public draw(graphics: Graphics) {
    const prevPriceLevel = {
      price: this.arrayToDraw[0]?.price || 0,
      sum: 0,
    };

    const xMax = this.x + this.width + this.firstStepSizeInPX;
    const yInitial = this.priceToY(prevPriceLevel.price);

    const isLargestSizeItem = (y: number) => y < this.y || y > this.y + this.height;

    // Check for no size or if the one renderable item would be out of bounds
    if (this.arrayToDraw[0]?.amount === 0 || isLargestSizeItem(yInitial)) {
      this.largestSizeItem = this.getZeroPriceLevel();
      this.lastVisibleItem = this.getZeroPriceLevel();

      return;
    }

    graphics
      .lineStyle(1, new Color(themingInstance.getColor(this.isAsk ? '--v-theme-error' : '--v-theme-success')).toNumber(), 1, 0)
      .beginFill(new Color(themingInstance.getColor(this.isAsk ? '--error-darkest' : '--success-darkest')).toNumber(), 1)
      .moveTo(xMax, yInitial);

    let largestSize = 0;

    for (let i = 0; i < this.arrayToDraw.length; i += 1) {
      const { amount, price, sum } = this.arrayToDraw[i] || { sum: 0, price: 0 };
      const y = this.priceToY(price);

      if (isLargestSizeItem(y)) {
        this.lastVisibleItem = this.arrayToDraw[i];

        break;
      }

      if (amount !== undefined && amount > largestSize) {
        largestSize = amount;
        this.largestSizeItem = this.arrayToDraw[i];
      }

      if (price !== prevPriceLevel.price) {
        graphics.lineTo(this.sizeToX(prevPriceLevel.sum), y);
      }

      if (sum !== prevPriceLevel.sum) {
        graphics.lineTo(this.sizeToX(sum), y);
      }

      prevPriceLevel.price = price;
      prevPriceLevel.sum = sum;
    }

    graphics
      .lineTo(this.sizeToX(prevPriceLevel.sum), this.isAsk ? this.y - 1 : this.height * 2 + 1)
      .lineTo(xMax, this.isAsk ? this.y - 1 : this.y + this.height + 1)
      .endFill();
  }

  public update(arrayToDraw: Array<DepthChartPriceLevel>, lastMatchedPrice: number): void {
    this.lastMatchedPrice = lastMatchedPrice;
    this.arrayToDraw = arrayToDraw;
  }

  public getMaxSize(priceWindow: number) {
    let maxSize = 0;

    this.deltaPrice = this.height / priceWindow;
    for (let i = 0; i < this.arrayToDraw.length; i += 1) {
      const { amount, price } = this.arrayToDraw[i] || { amount: 0, price: 0 };
      const y = this.priceToY(price);

      if (y < this.y || y > this.y + this.height) {
        break;
      }

      maxSize += amount;
    }

    return maxSize;
  }

  public setZoom(sizeWindow: number, priceWindow: number) {
    this.deltaSize = this.width / sizeWindow;
    this.deltaPrice = this.height / priceWindow;
  }

  public sizeToX(x: number) {
    return this.clampNumber(this.x + this.width - x * this.deltaSize - this.firstStepSizeInPX, this.x, this.x + this.width - this.firstStepSizeInPX);
  }

  public priceToY(y: number) {
    return this.y + (this.isAsk ? this.height : 1) + (this.lastMatchedPrice - y) * this.deltaPrice;
  }

  public yToPrice(y: number) {
    return -((y - this.y - (this.isAsk ? this.height : 1)) / this.deltaPrice - this.lastMatchedPrice);
  }

  // Clamp a number within the inclusive range specified by the given boundary values min and max
  private clampNumber(num: number, min: number, max: number) {
    if (num < min) {
      return min;
    }

    if (num > max) {
      return max;
    }

    return num;
  }

  private getZeroPriceLevel() {
    return {
      side: this.isAsk ? OrderbookSide.ASK : OrderbookSide.BID,
      price: 0,
      amount: 0,
      total: 0,
      sum: 0,
    };
  }
}
